import React, { useState, useRef, useEffect } from "react";

const ArrowUp = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 3L13 8H9.5V13H6.5V8H3L8 3Z" fill="#000000"/>
  </svg>
);

const ArrowDown = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 13L3 8H6.5V3H9.5V8H13L8 13Z" fill="#000000"/>
  </svg>
);

const RatioCard = ({ label, value, bg, positive, leaving }) => (
  <div
    style={{
      opacity: leaving ? 0 : 1,
      transform: leaving ? "scale(0.95)" : "scale(1)",
      transition: "opacity 0.25s ease, transform 0.25s ease",
    }}
  >
    <div
      style={{
        background: bg,
        borderRadius: "20px",
        padding: "24px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      <div style={{ fontSize: "14px", fontWeight: 400, color: "#000000", lineHeight: "20px" }}>
        {label}
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: "24px", fontWeight: 600, color: "#000000", lineHeight: "32px" }}>
          {value}
        </div>
        {positive !== undefined && (
          <div style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", color: "#000000" }}>
            {positive ? "+x%" : "-x%"}
            {positive ? <ArrowUp /> : <ArrowDown />}
          </div>
        )}
      </div>
    </div>
  </div>
);

export default function Ratios({ information = 0, trenor = 0, sharpe = 0 }) {
  const sortino = 1.6;
  const omega = 0.9;
  const green = "rgba(230, 253, 231, 0.8)";
  const red = "rgba(255, 159, 163, 0.9)";

  const [activeTab, setActiveTab] = useState("risk");
  const [isOpen, setIsOpen] = useState(false);
  const [visibleRatios, setVisibleRatios] = useState({
    "Sharpe Ratio": true,
    "Sortino ratio": true,
    "Information Ratio": true,
    "Omega Ratio": true,
    "Treynor Ratio": true,
  });
  const [leavingRatios, setLeavingRatios] = useState({});
  const [gridOpacity, setGridOpacity] = useState(1);

  const timersRef = useRef({});
  const rootRef = useRef(null);

  const riskCards = [
    { key: "sharpe",      label: "Sharpe Ratio",      value: sharpe.toFixed(2),      bg: green, positive: true  },
    { key: "sortino",     label: "Sortino ratio",      value: sortino.toFixed(2),     bg: green, positive: true  },
    { key: "information", label: "Information Ratio",  value: information.toFixed(2), bg: green, positive: true  },
    { key: "omega",       label: "Omega Ratio",        value: omega.toFixed(2),       bg: green, positive: true  },
    { key: "treynor",     label: "Treynor Ratio",      value: trenor.toFixed(2),      bg: red,   positive: false },
  ];

  const performanceCards = [
    { key: "hitrate",    label: "Hit-Rate",                  value: sharpe.toFixed(2),      bg: green, positive: true  },
    { key: "cents",      label: "Cents/share",               value: sortino.toFixed(2),     bg: green, positive: true  },
    { key: "payoff",     label: "Payoff Ratio",              value: information.toFixed(2), bg: green, positive: true  },
    { key: "holding",    label: "Avg Holding Period",        value: omega.toFixed(2),       bg: green, positive: true  },
    { key: "annualized", label: "Annualized avg return",     value: trenor.toFixed(2),      bg: red,   positive: false },
    { key: "ic",         label: "Information Coefficient",   value: omega.toFixed(2),       bg: green, positive: true  },
  ];

  const activeCards = activeTab === "risk" ? riskCards : performanceCards;

  // On tab switch: clear pending timers, reset visible/leaving state
  useEffect(() => {
    Object.values(timersRef.current).forEach(clearTimeout);
    timersRef.current = {};
    const allTrue = Object.fromEntries(activeCards.map(c => [c.label, true]));
    setVisibleRatios(allTrue);
    setLeavingRatios({});
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fade grid on tab switch: drop to 0, then restore after one frame
  useEffect(() => {
    setGridOpacity(0);
    const t = setTimeout(() => setGridOpacity(1), 50);
    return () => clearTimeout(t);
  }, [activeTab]);

  // Cleanup all timers on unmount
  useEffect(() => {
    const timers = timersRef.current;
    return () => Object.values(timers).forEach(clearTimeout);
  }, []);

  // Click-outside closes dropdown
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const toggleRatio = (label) => {
    const isCurrentlyVisible = visibleRatios[label];
    if (isCurrentlyVisible) {
      setLeavingRatios((prev) => ({ ...prev, [label]: true }));
      timersRef.current[label] = setTimeout(() => {
        setVisibleRatios((prev) => ({ ...prev, [label]: false }));
        setLeavingRatios((prev) => {
          const next = { ...prev };
          delete next[label];
          return next;
        });
      }, 250);
    } else {
      clearTimeout(timersRef.current[label]);
      setVisibleRatios((prev) => ({ ...prev, [label]: true }));
      setLeavingRatios((prev) => {
        const next = { ...prev };
        delete next[label];
        return next;
      });
    }
  };

  return (
    <div ref={rootRef} style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, position: "relative" }}>
      <style>{`
        .ratios-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 14px;
          flex-shrink: 0;
        }
        .ratios-dropdown-btn {
          background: rgba(73, 73, 73, 0.66);
          border: none;
          color: #ffffff;
          border-radius: 8px;
          padding: 4px 10px;
          font-size: 12px;
          cursor: pointer;
          white-space: nowrap;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .ratios-dropdown-btn:hover {
          background: rgba(90, 90, 90, 0.8);
        }
        .ratios-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }
        .ratios-dropdown {
          position: absolute;
          right: 0;
          top: 40px;
          z-index: 100;
          background: rgba(120, 120, 120, 0.95);
          border-radius: 16px;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          min-width: 200px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.4);
        }
        .ratios-dropdown-item {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          user-select: none;
        }
        .ratios-dropdown-item input[type="checkbox"] {
          width: 16px;
          height: 16px;
          cursor: pointer;
          accent-color: #1f1f1f;
          flex-shrink: 0;
        }
        .ratios-dropdown-item span {
          font-size: 15px;
          color: #ffffff;
          font-weight: 400;
        }
      `}</style>

      <div className="ratios-header">
        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
          <span
            onClick={() => setActiveTab("risk")}
            style={{
              cursor: "pointer",
              fontWeight: activeTab === "risk" ? 700 : 400,
              color: activeTab === "risk" ? "white" : "#888",
              fontSize: "14px",
              transition: "color 0.2s ease, font-weight 0.2s ease",
            }}
          >
            Risk Ratios
          </span>
          <span style={{ color: "#555", fontSize: "14px" }}>/</span>
          <span
            onClick={() => setActiveTab("performance")}
            style={{
              cursor: "pointer",
              fontWeight: activeTab === "performance" ? 700 : 400,
              color: activeTab === "performance" ? "white" : "#888",
              fontSize: "14px",
              transition: "color 0.2s ease, font-weight 0.2s ease",
            }}
          >
            Performance Ratios
          </span>
        </div>
        <button className="ratios-dropdown-btn" onClick={() => setIsOpen((o) => !o)}>
          Ratios ▾
        </button>
      </div>

      {isOpen && (
        <div className="ratios-dropdown">
          {activeCards.map(({ key, label }) => (
            <label key={key} className="ratios-dropdown-item">
              <input
                type="checkbox"
                checked={!!visibleRatios[label]}
                onChange={() => toggleRatio(label)}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
      )}

      <div
        className="ratios-grid"
        style={{ opacity: gridOpacity, transition: "opacity 0.2s ease" }}
      >
        {activeCards
          .filter(({ label }) => visibleRatios[label])
          .map(({ key, label, value, bg, positive }) => (
            <RatioCard
              key={key}
              label={label}
              value={value}
              bg={bg}
              positive={positive}
              leaving={!!leavingRatios[label]}
            />
          ))}
      </div>
    </div>
  );
}
